import { useEffect } from 'react';
import { useCanvasStore } from '../../store/canvas-store';
import type { ChatFrame, CodeFrame, Command, Frame } from '../../store/types';
import { isPanelOwned } from '../../store/types';
import { shortId } from '../dropDetect';

/**
 * Find the panel-owned tool frame of a given kind anchored to `anchorId`.
 * Pure — unit-tested. Ignores non-panel frames sharing the same anchor.
 */
export function findPanelFrame(
  frames: Frame[],
  kind: 'code' | 'chat',
  anchorId: string,
): CodeFrame | ChatFrame | undefined {
  return frames.find(
    (f): f is CodeFrame | ChatFrame =>
      f.kind === kind && isPanelOwned(f) && f.anchorId === anchorId,
  );
}

/** The single shared panel-owned code frame, regardless of anchor. */
function findPanelCode(frames: Frame[]): CodeFrame | undefined {
  return frames.find((f): f is CodeFrame => f.kind === 'code' && isPanelOwned(f));
}

/** The single shared global (whole-canvas) panel-owned chat, if it exists. */
export function findPanelGlobalChat(frames: Frame[]): ChatFrame | undefined {
  return frames.find(
    (f): f is ChatFrame => f.kind === 'chat' && isPanelOwned(f) && !f.anchorId,
  );
}

/**
 * Resolve the panel-owned chat for a target (anchorId set → frame chat,
 * omitted → global chat), creating it if missing. Returns the chat id. The new
 * id is generated locally so callers know it immediately. Unit-tested.
 */
export function ensurePanelChat(opts: {
  frames: Frame[];
  dispatch: (cmd: Command) => void;
  anchorId?: string;
}): string {
  const { frames, dispatch, anchorId } = opts;
  const existing = anchorId
    ? findPanelFrame(frames, 'chat', anchorId)
    : findPanelGlobalChat(frames);
  if (existing) return existing.id;
  const id = shortId();
  dispatch({
    t: 'toolFrame.create',
    id,
    toolKind: 'chat',
    x: -100000,
    y: -100000,
    anchorId,
    pinned: true,
    panelOwned: true,
  });
  return id;
}

/** Ensure + return the global panel-owned chat. Creation runs in an effect. */
export function useGlobalChat(): ChatFrame | undefined {
  const frames = useCanvasStore((s) => s.doc.frames);
  const dispatch = useCanvasStore((s) => s.dispatch);
  useEffect(() => {
    if (!findPanelGlobalChat(frames)) {
      ensurePanelChat({ frames, dispatch });
    }
  }, [frames, dispatch]);
  return findPanelGlobalChat(frames);
}

export interface PanelFrames {
  codeFrame?: CodeFrame;
  chatFrame?: ChatFrame;
}

/**
 * Resolve the panel-owned code + chat frames for the current target, creating
 * them lazily. The code frame is a single shared instance re-anchored to the
 * target (it is stateless). The chat frame is one-per-design-frame so each
 * frame keeps its own conversation history.
 *
 * Creation/re-anchoring runs in an effect (never during render).
 */
export function usePanelFrames(targetId: string | undefined): PanelFrames {
  const frames = useCanvasStore((s) => s.doc.frames);
  const dispatch = useCanvasStore((s) => s.dispatch);

  useEffect(() => {
    if (!targetId) return;
    const code = findPanelCode(frames);
    if (!code) {
      dispatch({
        t: 'toolFrame.create',
        id: shortId(),
        toolKind: 'code',
        x: -100000, // off-canvas; never rendered
        y: -100000,
        anchorId: targetId,
        panelOwned: true,
      });
    } else if (code.anchorId !== targetId) {
      dispatch({ t: 'toolFrame.setAnchor', id: code.id, anchorId: targetId });
    }

    if (!findPanelFrame(frames, 'chat', targetId)) {
      dispatch({
        t: 'toolFrame.create',
        id: shortId(),
        toolKind: 'chat',
        x: -100000,
        y: -100000,
        anchorId: targetId,
        pinned: true,
        panelOwned: true,
      });
    }
  }, [targetId, frames, dispatch]);

  if (!targetId) return {};
  return {
    codeFrame: findPanelCode(frames),
    chatFrame: findPanelFrame(frames, 'chat', targetId) as ChatFrame | undefined,
  };
}
